import React from 'react';
import PropTypes from 'prop-types';
import { arrayMove } from 'react-sortable-hoc';
import { withRouter } from 'react-router-dom';
import getFileName from 'utils/getFileName';

import Medialib from 'api/resources/medialib';
import getPrefix from 'utils/getPrefix';
import { processErrorByAlert } from 'utils/getErrors';

import IconClose from 'icons/Close';

import DeleteGallery from 'admin-editor/containers/DeleteGallery';

import ButtonLoader from 'components/Loader/ButtonLoader';
import GalleryPhotoList from 'admin-editor/components/GalleryPhotoList';
import LoadFileBlock from 'admin-editor/components/LoadFileBlock';
import TextBlock from 'admin-editor/components/TextBlock';

import FilterWrapper from 'styled/Page/FilterWrapper';
import RightColumn from 'styled/Page/RightColumn';
import CloseLink from 'styled/Page/CloseLink';
import RightColumnBody from 'styled/Page/RightColumnBody';
import PrimaryButton from 'styled/Buttons/PrimaryButton';
import SecondaryButton from 'styled/Buttons/SecondaryButton';

export class GalleryForm extends React.PureComponent {
  state = {
    title: this.props.title,
    galleryFiles: this.props.galleryFiles,
    loading: false,
  }

  get data() {
    return ({
      type: 'gallery',
      attributes: { title: this.state.title },
      relationships: {
        gallery_files: {
          data: this.state.galleryFiles.map(photo => ({
            type: 'image',
            attributes: photo.image ? {
              title: photo.title,
              url: photo.image.url,
              filename: photo.image.filename,
              file_type: photo.image.fileType,
              file_size: photo.image.fileSize,
              width: photo.image.width,
              height: photo.image.height,
              medialib_item_id: photo.image.medialibItemId,
            } : {},
          })),
        },
      },
    });
  }

  handleMedialibLoad = async (files) => {
    this.setState({ loading: true });

    try {
      const response = await Medialib.post({
        upload: files,
        prefix: getPrefix('galleries'),
      });

      this.setState(prevState => ({
        loading: false,
        galleryFiles: prevState.galleryFiles.concat(response.data.map(photo => ({
          title: photo.title || '',
          image: {
            url: photo.attributes.path,
            filename: getFileName({
              url: photo.attributes.path,
              type: '/galleries',
            }),
            fileType: photo.attributes.content_type,
            fileSize: photo.attributes.size,
            width: photo.attributes.width,
            height: photo.attributes.height,
            medialibItemId: photo.id,
          },
        }))),
      }));
    } catch (err) {
      this.setState({ loading: false });
      processErrorByAlert(err);
    }
  }

  handleSortEnd = ({ oldIndex, newIndex }) => {
    this.setState(prevState => ({
      galleryFiles: arrayMove(prevState.galleryFiles, oldIndex, newIndex),
    }));
  };

  handlePhotoRemove = (order) => {
    this.setState(prevState => ({
      galleryFiles: prevState.galleryFiles.filter((photo, index) => index !== order),
    }));
  }

  handleTitleChange = (e) => {
    this.setState({ title: e.target.value });
  }

  handlePhotoTitleChange = (value, order) => {
    this.setState(prevState => ({
      galleryFiles: prevState.galleryFiles.map((photo, index) => {
        if (index === order) return { ...photo, title: value };
        return photo;
      }),
    }));
  }

  handleSaveClick = () => {
    this.props.onSubmit({ data: this.data });
  }

  handleSaveAndExitClick = () => {
    this.props.onSubmit({
      data: this.data,
      isExit: true,
    });
  }

  render() {
    return (
      <React.Fragment>
        <div style={{ width: '100%' }}>
          <FilterWrapper style={{ justifyContent: 'flex-end' }}>
            <PrimaryButton
              type="button"
              onClick={this.handleSaveClick}
              style={{ width: '116px' }}
            >
              {this.props.saveLoading ? <ButtonLoader /> : 'сохранить'}
            </PrimaryButton>

            <SecondaryButton
              type="button"
              style={{ marginLeft: '20px' }}
              onClick={this.handleSaveAndExitClick}
            >
              сохранить и выйти
            </SecondaryButton>

            <CloseLink to="/galleries"><IconClose /></CloseLink>
          </FilterWrapper>

          <div style={{ padding: '30px 32px 0 24px' }}>
            <TextBlock
              value={this.state.title}
              onChange={this.handleTitleChange}
              label="Заголовок"
            />

            <LoadFileBlock
              label="Фото"
              onLoad={this.handleMedialibLoad}
              loading={this.state.loading}
            />

            {this.state.galleryFiles.length !== 0 && (
              <GalleryPhotoList
                galleryFiles={this.state.galleryFiles}
                onSortEnd={this.handleSortEnd}
                distance={10}
                errors={this.props.errors}
                helperClass="sortable-photo_active"
                onRemove={this.handlePhotoRemove}
                onTitleChange={this.handlePhotoTitleChange}
              />
            )}
          </div>
        </div>

        <RightColumn style={{ paddingRight: '32px' }}>
          {this.props.match.params.galleryId && (
            <RightColumnBody style={{ height: '100%' }}>
              <DeleteGallery />
            </RightColumnBody>
          )}
        </RightColumn>
      </React.Fragment>
    );
  }
}

GalleryForm.propTypes = {
  saveLoading: PropTypes.bool,
  title: PropTypes.string,
  onSubmit: PropTypes.func,
  galleryFiles: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.shape({
      fileSize: PropTypes.number,
      fileType: PropTypes.string,
      filename: PropTypes.string,
      height: PropTypes.number,
      id: PropTypes.string,
      medialibItemId: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
      width: PropTypes.number,
    }),
    title: PropTypes.string,
  })),
  errors: PropTypes.arrayOf(PropTypes.string),
  match: PropTypes.shape({
    params: PropTypes.shape({
      galleryId: PropTypes.string,
    }),
  }),
};

GalleryForm.defaultProps = {
  title: '',
  onSubmit: () => {},
  galleryFiles: [],
  errors: [],
  saveLoading: false,
  match: {
    params: {
      galleryId: '',
    },
  },
};

export default withRouter(GalleryForm);
